import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!orderId) {
      setError('No se encontró el ID de la orden');
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        const response = await fetch(`${API}/orders/${orderId}`);
        if (!response.ok) {
          throw new Error('Error obteniendo la orden');
        }
        const data = await response.json();
        setOrder(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    // Poll for updates every 3 seconds for up to 30 seconds
    const interval = setInterval(fetchOrder, 3000);
    setTimeout(() => clearInterval(interval), 30000);

    return () => clearInterval(interval);
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen py-20 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Verificando el estado de tu pago...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen py-20 px-4">
        <div className="container mx-auto max-w-2xl">
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-12 text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-4">Error</h1>
              <p className="text-slate-400 mb-6">{error || 'No se pudo obtener la información de la orden'}</p>
              <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-cyan-600 to-emerald-600">
                Volver al Inicio
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getStatusContent = () => {
    switch (order.status) {
      case 'approved':
        return {
          icon: <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />,
          title: '¡Pago Aprobado!',
          message: 'Tu pago ha sido procesado exitosamente. Recibirás un email de confirmación.',
          color: 'text-green-400'
        };
      case 'pending':
        return {
          icon: <Clock className="h-20 w-20 text-yellow-500 mx-auto mb-6" />,
          title: 'Pago Pendiente',
          message: 'Tu pago está siendo procesado. Te notificaremos cuando se acredite.',
          color: 'text-yellow-400'
        };
      case 'cancelled':
        return {
          icon: <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />,
          title: 'Pago Rechazado',
          message: 'Tu pago fue rechazado. Puedes intentar nuevamente.',
          color: 'text-red-400'
        };
      default:
        return {
          icon: <Clock className="h-20 w-20 text-slate-500 mx-auto mb-6" />,
          title: 'Procesando',
          message: 'Estamos verificando el estado de tu pago.',
          color: 'text-slate-400'
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container mx-auto max-w-3xl">
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-12">
            <div className="text-center mb-8">
              {statusContent.icon}
              <h1 className={`text-4xl font-bold mb-4 ${statusContent.color}`}>
                {statusContent.title}
              </h1>
              <p className="text-slate-300 text-lg mb-6">
                {statusContent.message}
              </p>
            </div>

            <Card className="bg-slate-900/50 border-slate-700 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Detalles de la Orden</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Número de Orden:</span>
                    <span className="text-white font-semibold">{order.order_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Cliente:</span>
                    <span className="text-white">{order.customer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Email:</span>
                    <span className="text-white">{order.customer_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Entrega:</span>
                    <span className="text-white">
                      {order.delivery_method === 'pickup' ? 'Retiro en local' : 'Envío a domicilio'}
                    </span>
                  </div>
                  {order.shipping_address && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Dirección:</span>
                      <span className="text-white text-right">{order.shipping_address}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-3 mt-3">
                    <div className="flex justify-between text-lg">
                      <span className="text-white font-bold">Total:</span>
                      <span className="text-cyan-400 font-bold text-2xl">
                        ${order.total_amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="border-slate-700 text-slate-300"
              >
                Volver al Inicio
              </Button>
              <Button 
                onClick={() => navigate('/shop')} 
                className="bg-gradient-to-r from-cyan-600 to-emerald-600"
              >
                Seguir Comprando
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentResult;